import { BarChart3, BookOpen, Clock, Copy, Edit, GraduationCap, LayoutDashboard, Library, Loader2, LogOut, Play, Plus, Radio, Search, Settings, User, Users, MonitorPlay, Eye, Share2, LayoutGrid, List, SlidersHorizontal, Sparkles, CalendarDays, Layers, ChevronLeft, ChevronRight, Filter, Trash2 } from 'lucide-react';
import { getCurrentUser, supabase } from '../../utils/supabase/client';
import { getTeacherLessons, getTeacherLessonsIncludingShared, createLesson, createSession, duplicateLessonContent, getTeacherActiveSessions } from '../../utils/supabase/queries';
import type { Lesson } from '../../utils/supabase/client';
import { signOut } from '../../utils/supabase/auth';
import { getSessionWithRetry, handleOAuthCallback } from '../../utils/supabase/oauth-handler';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import Gradebook from './Gradebook';
import Reports from './Reports';
import Students from './Students';
import SettingsPage from './Settings';
import { CreateLessonModal } from '../components/CreateLessonModal';
import { StartLessonModal } from '../components/StartLessonModal';
import { LessonModeBadge } from '../components/LessonModeBadge';
import { ShareLessonModal } from '../components/ShareLessonModal';

interface LessonWithStats extends Lesson {
  slideCount?: number;
  studentCount?: number;
}

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<LessonWithStats | null>(null);
  const [duplicatingLessonId, setDuplicatingLessonId] = useState<string | null>(null);
  
  // Data state
  const [lessons, setLessons] = useState<LessonWithStats[]>([]);
  const [activeSessions, setActiveSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [teacherProfile, setTeacherProfile] = useState<any>(null);
  const [profileError, setProfileError] = useState(false);
  const [page, setPage] = useState(0);
  const [totalLessons, setTotalLessons] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title'>('newest');
  const pageSize = 20;

  // Load teacher profile
  useEffect(() => {
    loadTeacherProfile();
  }, []);

  // Load lessons when component mounts or page changes
  useEffect(() => {
    if (teacherProfile) {
      loadLessons();
      loadActiveSessions();
      // Refresh active sessions every 10 seconds
      const refreshInterval = setInterval(loadActiveSessions, 10000);
      return () => clearInterval(refreshInterval);
    }
  }, [teacherProfile, page]);

  const loadTeacherProfile = async () => {
    try {
      console.log('Loading teacher profile...');
      
      // Handle OAuth callback if we're coming from redirect with auth parameters
      if (window.location.hash.includes('access_token')) {
        console.log('OAuth callback detected, processing...');
        const oauthResult = await handleOAuthCallback();
        if (!oauthResult.success) {
          console.warn('OAuth callback processing warning:', oauthResult.message);
          // Continue anyway - might still have a session
        }
        // Ensure hash is cleaned even if handleOAuthCallback didn't clean it
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
      
      // Try to get session with retry logic (handles OAuth timing issues)
      const session = await getSessionWithRetry(3, 100);
      
      if (!session) {
        console.log('No active session, redirecting to login');
        navigate('/teacher/login');
        return;
      }

      console.log('Session found, user ID:', session.user?.id);

      // Get user from session
      const user = session.user;
      if (!user) {
        navigate('/teacher/login');
        return;
      }

      // Try to get existing profile
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, full_name, role, avatar_url, email')
        .eq('id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "not found" which is okay
        throw error;
      }

      // If profile doesn't exist, create it
      if (!profile) {
        console.log('Profile not found, creating new teacher profile...');
        
        const newProfile = {
          id: user.id,
          full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Teacher',
          email: user.email,
          role: 'teacher',
          avatar_url: user.user_metadata?.avatar_url || null
        };

        const { data: createdProfile, error: createError } = await supabase
          .from('profiles')
          .insert(newProfile)
          .select('id, full_name, role, avatar_url')
          .single();

        if (createError) {
          console.error('Error creating profile:', createError);
          throw createError;
        }

        setTeacherProfile(createdProfile);
        toast.success('Welcome! Your profile has been created.');
        return;
      }

      // Verify user is a teacher
      if (profile.role !== 'teacher') {
        toast.error('Access denied. Teachers only.');
        await signOut();
        navigate('/');
        return;
      }

      setTeacherProfile(profile);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile. Please try again.');
      setProfileError(true);
      // Don't navigate away immediately - let user try again
    }
  };

  const loadLessons = async () => {
    if (!teacherProfile) return;

    setLoading(true);
    try {
      // Ensure we have a valid session before querying
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session available for loading lessons');
        setLoading(false);
        return;
      }

      const { lessons: data, count } = await getTeacherLessonsIncludingShared(teacherProfile.id, {
        limit: pageSize,
        offset: page * pageSize
      });

      // Fetch slide counts for each lesson
      const lessonsWithStats: LessonWithStats[] = await Promise.all(
        data.map(async (lesson: any) => {
          // Get slide count
          const { count: slideCount } = await supabase
            .from('slides')
            .select('id', { count: 'exact', head: true })
            .eq('lesson_id', lesson.id);

          // Get student count (participants across all sessions for this lesson)
          const { data: sessionIds } = await supabase
            .from('sessions')
            .select('id')
            .eq('lesson_id', lesson.id);

          const { count: studentCount } = await supabase
            .from('session_participants')
            .select('id', { count: 'exact', head: true })
            .in(
              'session_id',
              (sessionIds || []).map(s => s.id)
            );

          return {
            id: lesson.id,
            title: lesson.title,
            description: lesson.description,
            mode: lesson.mode,
            status: lesson.status,
            cover_image: lesson.cover_image,
            teacher_id: lesson.teacher_id,
            classroom_id: lesson.classroom_id,
            created_at: lesson.created_at,
            updated_at: lesson.updated_at,
            slideCount: slideCount || 0,
            studentCount: studentCount || 0,
            is_shared: lesson.is_shared || false,
            shared_by: lesson.shared_by || null
          } as LessonWithStats & { is_shared?: boolean; shared_by?: string | null };
        })
      );

      setLessons(lessonsWithStats);
      setTotalLessons(count);
    } catch (error: any) {
      console.error('Error loading lessons:', error);
      
      // Only show error toast if it's not a session/auth issue
      // (those are handled by redirecting to login)
      if (error?.code !== 'PGRST301' && !error?.message?.includes('JWT')) {
        toast.error('Failed to load lessons');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadActiveSessions = async () => {
    if (!teacherProfile) return;

    try {
      const sessions = await getTeacherActiveSessions(teacherProfile.id);
      setActiveSessions(sessions);
    } catch (error) {
      console.error('Error loading active sessions:', error);
      // Don't show toast for this, as it's a non-critical background load
    }
  };

  const handleCreateLesson = async (lessonData: any) => {
    if (!teacherProfile) return;

    try {
      const newLesson = await createLesson({
        teacher_id: teacherProfile.id,
        title: lessonData.title,
        description: lessonData.description || '',
        mode: lessonData.mode,
        classroom_id: lessonData.classroom_id || null,
        cover_image: lessonData.coverImage || null
      });

      setShowCreateModal(false);
      toast.success('Lesson created successfully!');
      
      // Refresh lessons
      await loadLessons();
      
      // Navigate to lesson builder
      setTimeout(() => {
        navigate(`/teacher/lesson/${newLesson.id}`);
      }, 500);
    } catch (error) {
      console.error('Error creating lesson:', error);
      toast.error('Failed to create lesson');
    }
  };

  const handleDuplicateLesson = async (lesson: LessonWithStats) => {
    if (!teacherProfile || duplicatingLessonId) return;

    setDuplicatingLessonId(lesson.id);
    try {
      const duplicatedLesson = await duplicateLessonContent(lesson.id, teacherProfile.id);
      toast.success('Lesson duplicated. You can edit the copy now.');
      await loadLessons();
      navigate(`/teacher/lesson/${duplicatedLesson.id}`);
    } catch (error) {
      console.error('Error duplicating lesson:', error);
      toast.error('Failed to duplicate lesson');
    } finally {
      setDuplicatingLessonId(null);
    }
  };

  const handleStartLesson = (lesson: LessonWithStats) => {
    setSelectedLesson(lesson);
    setShowStartModal(true);
  };

  const handleShareLesson = (lesson: LessonWithStats) => {
    setSelectedLesson(lesson);
    setShowShareModal(true);
  };

  const handleConfirmStart = async (config: any) => {
    if (!selectedLesson || !teacherProfile) return;

    try {
      // Create a new session
      const session = await createSession({
        lesson_id: selectedLesson.id,
        teacher_id: teacherProfile.id,
        mode: selectedLesson.mode,
        session_code: config.sessionCode, // Pass the client-generated code
        due_date: config.dueDate
      });

      setShowStartModal(false);

      if (selectedLesson.mode === 'live') {
        toast.success(`Live session started! Code: ${session.session_code}`);
        navigate(`/teacher/live/${session.id}`);
      } else {
        toast.success(`Student-paced session created! Code: ${session.session_code}`);
        navigate(`/teacher/student-paced/${session.id}`);
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start session');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/teacher/login');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  const handleMonitorSession = (sessionId: string, mode: string) => {
    if (mode === 'live') {
      navigate(`/teacher/live/${sessionId}`);
    } else {
      navigate(`/teacher/student-paced/${sessionId}`);
    }
  };

  const filteredLessons = lessons
    .filter(lesson =>
      lesson.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lesson.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      if (sortBy === 'oldest') return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      return a.title.localeCompare(b.title);
    });

  const sidebarItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'lessons', icon: BookOpen, label: 'Lessons' },
    { id: 'gradebook', icon: GraduationCap, label: 'Gradebook' },
    { id: 'reports', icon: BarChart3, label: 'Reports' },
    /*{ id: 'library', icon: Library, label: 'Library' },*/
    { id: 'students', icon: Users, label: 'Students' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  const getThumbnailColor = (index: number) => {
    const colors = [
      'bg-gradient-to-br from-green-400 to-emerald-600',
      'bg-gradient-to-br from-blue-400 to-indigo-600',
      'bg-gradient-to-br from-purple-400 to-pink-600',
      'bg-gradient-to-br from-orange-400 to-red-600',
      'bg-gradient-to-br from-indigo-400 to-purple-600',
      'bg-gradient-to-br from-cyan-400 to-blue-600',
    ];
    return colors[index % colors.length];
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Profile Error State */}
      {profileError && !teacherProfile && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Failed to Load Profile</h2>
            <p className="text-muted-foreground mb-6">
              We couldn't load your teacher profile. This might be a database connection issue.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={loadTeacherProfile} variant="default">
                <Loader2 className="w-4 h-4 mr-2" />
                Retry
              </Button>
              <Button onClick={handleSignOut} variant="outline">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Normal Dashboard */}
      {!profileError && (
        <>
          {/* Sidebar */}
          <aside className="w-64 bg-card border-r border-border flex flex-col">
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold">EduChemMix</h1>
                  <p className="text-xs text-muted-foreground">JHS</p>
                </div>
              </div>
            </div>

            <nav className="flex-1 p-4 space-y-1">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSearchParams({ tab: item.id })}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    activeTab === item.id
                      ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>

            <div className="p-4 border-t border-border">
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-sidebar-accent">
                {teacherProfile?.avatar_url ? (
                  <img
                    src={teacherProfile.avatar_url}
                    alt={teacherProfile.full_name}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {teacherProfile?.full_name || 'Teacher'}
                  </p>
                  <p className="text-xs text-muted-foreground">Teacher</p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-muted-foreground hover:text-foreground"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 overflow-auto">
            {activeTab === 'gradebook' ? (
              <Gradebook />
            ) : activeTab === 'reports' ? (
              <Reports />
            ) : activeTab === 'students' ? (
              <Students />
            ) : activeTab === 'settings' ? (
              <SettingsPage />
            ) : (
              <div className="p-8">
                {/* Header */}
                {activeTab === 'lessons' ? (
                  <div className="relative mb-8 -mx-8 -mt-8 px-8 pt-8 pb-8 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-primary/4 to-transparent" />
                    <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" />
                    <div className="relative">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-primary" />
                            </div>
                            <h1 className="text-3xl font-bold tracking-tight">My Lessons</h1>
                          </div>
                          <p className="text-muted-foreground ml-[52px]">
                            Create and manage your interactive lessons
                          </p>
                        </div>
                        <Button
                          onClick={() => setShowCreateModal(true)}
                          size="lg"
                          className="rounded-xl shadow-lg shadow-primary/25 font-semibold h-12 px-6"
                        >
                          <Plus className="w-5 h-5 mr-2" />
                          Create Lesson
                        </Button>
                      </div>

                      {/* Quick Stats Bar */}
                      <div className="flex items-center gap-6 mt-6 ml-[52px]">
                        <div className="flex items-center gap-2 text-sm">
                          <Layers className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold">{totalLessons}</span>
                          <span className="text-muted-foreground">lessons</span>
                        </div>
                        <div className="w-px h-4 bg-border" />
                        <div className="flex items-center gap-2 text-sm">
                          <Sparkles className="w-4 h-4 text-emerald-500" />
                          <span className="font-semibold">{lessons.filter(l => l.status === 'published').length}</span>
                          <span className="text-muted-foreground">published</span>
                        </div>
                        <div className="w-px h-4 bg-border" />
                        <div className="flex items-center gap-2 text-sm">
                          <Edit className="w-4 h-4 text-orange-500" />
                          <span className="font-semibold">{lessons.filter(l => l.status !== 'published').length}</span>
                          <span className="text-muted-foreground">drafts</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h1 className="text-3xl font-bold">Dashboard</h1>
                      <p className="text-muted-foreground mt-1">
                        Welcome back! Here's your teaching overview
                      </p>
                    </div>
                  </div>
                )}

                {/* Dashboard Stats */}
                {activeTab === 'dashboard' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-card rounded-2xl p-6 border border-border">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-primary" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">Total Lessons</p>
                      </div>
                      <p className="text-3xl font-bold">{totalLessons}</p>
                    </div>

                    <div className="bg-card rounded-2xl p-6 border border-border">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                          <Radio className="w-5 h-5 text-accent" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">Live Sessions</p>
                      </div>
                      <p className="text-3xl font-bold">
                        {lessons.filter(l => l.mode === 'live').length}
                      </p>
                    </div>

                    <div className="bg-card rounded-2xl p-6 border border-border">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-orange/10 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-orange" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">Student-Paced</p>
                      </div>
                      <p className="text-3xl font-bold">
                        {lessons.filter(l => l.mode === 'student-paced').length}
                      </p>
                    </div>

                    <div className="bg-card rounded-2xl p-6 border border-border">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                          <Users className="w-5 h-5 text-emerald-600" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">Total Students</p>
                      </div>
                      <p className="text-3xl font-bold">
                        {lessons.reduce((sum, l) => sum + (l.studentCount || 0), 0)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Active Sessions Section */}
                {activeTab === 'dashboard' && activeSessions.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <MonitorPlay className="w-5 h-5 text-primary" />
                      <h2 className="text-xl font-semibold">Active Monitoring Sessions</h2>
                      <Badge variant="default" className="ml-2">
                        {activeSessions.length} active
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {activeSessions.map((session: any) => (
                        <div
                          key={session.id}
                          className="bg-card rounded-xl border border-border p-4 hover:border-primary/50 transition-all duration-300 hover:shadow-md hover:shadow-primary/10"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="font-semibold text-base line-clamp-1">
                                {session.lessons?.title || 'Untitled Lesson'}
                              </h3>
                              <p className="text-xs text-muted-foreground mt-1">
                                Session Code: <span className="font-mono font-semibold">{session.session_code}</span>
                              </p>
                              {session.due_date && session.mode === 'student-paced' && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Ends: <span className="font-semibold">{new Date(session.due_date).toLocaleString()}</span>
                                </p>
                              )}
                            </div>
                            <MonitorPlay className="w-5 h-5 text-primary flex-shrink-0 ml-2" />
                          </div>
                          <div className="flex items-center justify-between mb-4">
                            <LessonModeBadge mode={session.mode} />
                            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200">
                              <div className="w-2 h-2 rounded-full bg-green-500 mr-2" />
                              Active
                            </Badge>
                          </div>
                          <Button
                            onClick={() => handleMonitorSession(session.id, session.mode)}
                            size="sm"
                            className="w-full"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Monitor Now
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search & Filter Bar */}
                {(activeTab === 'lessons' || activeTab === 'dashboard') && (
                  <div className="mb-6">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search lessons..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 h-11 rounded-xl border-border/60 focus:border-primary/50"
                        />
                      </div>

                      {activeTab === 'lessons' && (
                        <>
                          {/* Sort Dropdown */}
                          <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'title')}
                            className="h-11 px-3 rounded-xl border border-border/60 bg-background text-sm font-medium text-foreground focus:outline-none focus:border-primary/50 cursor-pointer"
                          >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="title">A → Z</option>
                          </select>

                          {/* View Toggle */}
                          <div className="flex items-center gap-0.5 bg-muted/60 rounded-xl p-1 border border-border/40">
                            <button
                              onClick={() => setViewMode('grid')}
                              className={`p-2 rounded-lg transition-all ${
                                viewMode === 'grid'
                                  ? 'bg-background shadow-sm text-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                              title="Grid view"
                            >
                              <LayoutGrid className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setViewMode('list')}
                              className={`p-2 rounded-lg transition-all ${
                                viewMode === 'list'
                                  ? 'bg-background shadow-sm text-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                              title="List view"
                            >
                              <List className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Active search indicator */}
                    {searchQuery && (
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-sm text-muted-foreground">
                          {filteredLessons.length} result{filteredLessons.length !== 1 ? 's' : ''} for
                        </span>
                        <span className="text-sm font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-md">
                          "{searchQuery}"
                        </span>
                        <button
                          onClick={() => setSearchQuery('')}
                          className="text-xs text-muted-foreground hover:text-foreground underline"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Loading State */}
                {loading && (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                )}

                {/* Lessons Grid */}
                {!loading && (
                  <>
                    {filteredLessons.length === 0 ? (
                      <div className="text-center py-20">
                        <div className="relative inline-block mb-6">
                          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center mx-auto">
                            <BookOpen className="w-12 h-12 text-primary/40" />
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Plus className="w-4 h-4 text-primary/50" />
                          </div>
                        </div>
                        <h3 className="text-xl font-bold mb-2">
                          {searchQuery ? 'No matches found' : 'No lessons yet'}
                        </h3>
                        <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                          {searchQuery
                            ? `No lessons match "${searchQuery}". Try a different search term.`
                            : 'Create your first interactive lesson to engage your students with EduChemMix'}
                        </p>
                        {!searchQuery && (
                          <Button onClick={() => setShowCreateModal(true)} size="lg" className="rounded-xl shadow-lg shadow-primary/25 font-semibold">
                            <Sparkles className="w-5 h-5 mr-2" />
                            Create Your First Lesson
                          </Button>
                        )}
                      </div>
                    ) : viewMode === 'list' && activeTab === 'lessons' ? (
                      /* List View */
                      <div className="space-y-3">
                        {filteredLessons.map((lesson, index) => (
                          <div
                            key={lesson.id}
                            className="bg-card rounded-xl border border-border/60 overflow-hidden hover:border-primary/40 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 group"
                          >
                            <div className="flex items-center gap-4 p-4">
                              {/* Thumbnail */}
                              {lesson.cover_image ? (
                                <img
                                  src={lesson.cover_image}
                                  alt={lesson.title}
                                  className="w-20 h-14 rounded-lg object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className={`w-20 h-14 rounded-lg ${getThumbnailColor(index)} flex items-center justify-center flex-shrink-0`}>
                                  <BookOpen className="w-6 h-6 text-white opacity-80" />
                                </div>
                              )}

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-base truncate">{lesson.title}</h3>
                                  {(lesson as any).is_shared && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0">
                                      <Share2 className="w-3 h-3 mr-1" />
                                      Shared
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground truncate">
                                  {lesson.description || 'No description'}
                                </p>
                              </div>

                              {/* Meta */}
                              <div className="hidden md:flex items-center gap-4 text-sm text-muted-foreground flex-shrink-0">
                                <div className="flex items-center gap-1.5">
                                  <Layers className="w-3.5 h-3.5" />
                                  <span>{lesson.slideCount || 0}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Users className="w-3.5 h-3.5" />
                                  <span>{lesson.studentCount || 0}</span>
                                </div>
                              </div>

                              {/* Badges */}
                              <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
                                <LessonModeBadge mode={lesson.mode} size="sm" />
                                <Badge variant={lesson.status === 'published' ? 'default' : 'secondary'} className="text-xs">
                                  {lesson.status === 'published' ? 'Published' : 'Draft'}
                                </Badge>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {!(lesson as any).is_shared && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-lg"
                                    onClick={() => navigate(`/teacher/lesson/${lesson.id}`)}
                                  >
                                    <Edit className="w-3.5 h-3.5 mr-1.5" />
                                    Edit
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDuplicateLesson(lesson)}
                                  title="Duplicate lesson"
                                  aria-label={`Duplicate ${lesson.title}`}
                                  disabled={duplicatingLessonId !== null}
                                  className="rounded-lg w-8 h-8 p-0"
                                >
                                  {duplicatingLessonId === lesson.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleShareLesson(lesson)}
                                  title="Share this lesson"
                                  className="rounded-lg w-8 h-8 p-0"
                                >
                                  <Share2 className="w-3.5 h-3.5" />
                                </Button>
                                {lesson.mode === 'student-paced' && activeSessions.find(s => s.lesson_id === lesson.id) ? (
                                  <Button
                                    size="sm"
                                    className="rounded-lg"
                                    onClick={() => {
                                      const activeSession = activeSessions.find(s => s.lesson_id === lesson.id);
                                      if (activeSession) handleMonitorSession(activeSession.id, activeSession.mode);
                                    }}
                                  >
                                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                                    Monitor
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    className="rounded-lg"
                                    onClick={() => handleStartLesson(lesson)}
                                    disabled={lesson.status !== 'published' || (lesson.slideCount || 0) === 0}
                                  >
                                    <Play className="w-3.5 h-3.5 mr-1.5" />
                                    Start
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Active session indicator */}
                            {lesson.mode === 'student-paced' && activeSessions.find(s => s.lesson_id === lesson.id) && (
                              <div className="px-4 pb-3 -mt-1">
                                <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 font-medium">
                                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                  Session active
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Grid View */
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredLessons.map((lesson, index) => (
                          <div
                            key={lesson.id}
                            className="bg-card rounded-2xl border border-border/60 overflow-hidden hover:border-primary/40 transition-all duration-300 hover:shadow-xl hover:shadow-primary/8 group"
                          >
                            {/* Thumbnail */}
                            <div className="relative overflow-hidden">
                              {lesson.cover_image ? (
                                <img
                                  src={lesson.cover_image}
                                  alt={lesson.title}
                                  className="h-36 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : (
                                <div className={`h-36 ${getThumbnailColor(index)} flex items-center justify-center relative`}>
                                  <BookOpen className="w-12 h-12 text-white opacity-80 transition-transform duration-300 group-hover:scale-110" />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                                </div>
                              )}
                              {/* Status pill on thumbnail */}
                              <div className="absolute top-3 left-3">
                                <Badge
                                  variant={lesson.status === 'published' ? 'default' : 'secondary'}
                                  className="shadow-md text-[11px] font-semibold"
                                >
                                  {lesson.status === 'published' ? 'Published' : 'Draft'}
                                </Badge>
                              </div>
                              {/* Active session glow */}
                              {lesson.mode === 'student-paced' && activeSessions.find(s => s.lesson_id === lesson.id) && (
                                <div className="absolute top-3 right-3">
                                  <div className="flex items-center gap-1.5 bg-green-500 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-lg">
                                    <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                    Live
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Content */}
                            <div className="p-5">
                              <div className="mb-3">
                                <div className="flex items-start justify-between gap-2">
                                  <h3 className="font-bold text-base mb-1 line-clamp-1 group-hover:text-primary transition-colors">
                                    {lesson.title}
                                  </h3>
                                  {(lesson as any).is_shared && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex-shrink-0 text-[10px]">
                                      <Share2 className="w-3 h-3 mr-0.5" />
                                      Shared
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                                  {lesson.description || 'No description added'}
                                </p>
                              </div>

                              <div className="flex items-center gap-2 mb-4">
                                <LessonModeBadge mode={lesson.mode} size="sm" />
                              </div>

                              {/* Stats Row */}
                              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4 py-3 border-t border-border/40">
                                <div className="flex items-center gap-1.5">
                                  <Layers className="w-3.5 h-3.5" />
                                  <span className="font-medium">{lesson.slideCount || 0}</span> slides
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Users className="w-3.5 h-3.5" />
                                  <span className="font-medium">{lesson.studentCount || 0}</span> students
                                </div>
                                <div className="flex items-center gap-1.5 ml-auto">
                                  <CalendarDays className="w-3.5 h-3.5" />
                                  {getTimeAgo(lesson.updated_at)}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex gap-2">
                                {!(lesson as any).is_shared && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 rounded-lg border-border/60 hover:bg-muted/60"
                                    onClick={() => navigate(`/teacher/lesson/${lesson.id}`)}
                                  >
                                    <Edit className="w-3.5 h-3.5 mr-1.5" />
                                    Edit
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDuplicateLesson(lesson)}
                                  title="Duplicate lesson"
                                  aria-label={`Duplicate ${lesson.title}`}
                                  disabled={duplicatingLessonId !== null}
                                  className="w-9 h-9 p-0 rounded-lg border-border/60 hover:bg-muted/60"
                                >
                                  {duplicatingLessonId === lesson.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleShareLesson(lesson)}
                                  title="Share this lesson with other teachers"
                                  className="w-9 h-9 p-0 rounded-lg border-border/60 hover:bg-muted/60"
                                >
                                  <Share2 className="w-3.5 h-3.5" />
                                </Button>
                                {lesson.mode === 'student-paced' && activeSessions.find(s => s.lesson_id === lesson.id) ? (
                                  <Button
                                    size="sm"
                                    className="flex-1 rounded-lg font-medium"
                                    onClick={() => {
                                      const activeSession = activeSessions.find(s => s.lesson_id === lesson.id);
                                      if (activeSession) handleMonitorSession(activeSession.id, activeSession.mode);
                                    }}
                                  >
                                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                                    Monitor
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    className="flex-1 rounded-lg font-medium"
                                    onClick={() => handleStartLesson(lesson)}
                                    disabled={lesson.status !== 'published' || (lesson.slideCount || 0) === 0}
                                  >
                                    <Play className="w-3.5 h-3.5 mr-1.5" />
                                    Start
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pagination */}
                    {totalLessons > pageSize && (
                      <div className="flex items-center justify-between mt-8 pt-6 border-t border-border/40">
                        <p className="text-sm text-muted-foreground">
                          Showing <span className="font-medium text-foreground">{page * pageSize + 1}</span> to <span className="font-medium text-foreground">{Math.min((page + 1) * pageSize, totalLessons)}</span> of <span className="font-medium text-foreground">{totalLessons}</span> lessons
                        </p>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(Math.max(0, page - 1))}
                            disabled={page === 0}
                            className="rounded-lg h-9 w-9 p-0"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          {Array.from({ length: Math.min(Math.ceil(totalLessons / pageSize), 5) }, (_, i) => {
                            const totalPages = Math.ceil(totalLessons / pageSize);
                            let pageNum = i;
                            if (totalPages > 5) {
                              const start = Math.max(0, Math.min(page - 2, totalPages - 5));
                              pageNum = start + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={page === pageNum ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setPage(pageNum)}
                                className={`rounded-lg h-9 w-9 p-0 font-medium ${
                                  page === pageNum ? 'shadow-md shadow-primary/20' : ''
                                }`}
                              >
                                {pageNum + 1}
                              </Button>
                            );
                          })}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(page + 1)}
                            disabled={(page + 1) * pageSize >= totalLessons}
                            className="rounded-lg h-9 w-9 p-0"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Other tabs placeholder */}
                {activeTab !== 'dashboard' && activeTab !== 'lessons' && activeTab !== 'gradebook' && (
                  <div className="text-center py-20">
                    <p className="text-muted-foreground">
                      {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} section coming soon
                    </p>
                  </div>
                )}
              </div>
            )}
          </main>

          {/* Modals */}
          {showCreateModal && (
            <CreateLessonModal
              open={showCreateModal}
              onCreateLesson={handleCreateLesson}
              onOpenChange={setShowCreateModal}
            />
          )}

          {showStartModal && selectedLesson && (
            <StartLessonModal
              open={showStartModal}
              onOpenChange={setShowStartModal}
              lessonTitle={selectedLesson.title}
              mode={selectedLesson.mode as 'live' | 'student-paced'}
              onStart={handleConfirmStart}
            />
          )}

          {showShareModal && selectedLesson && (
            <ShareLessonModal
              open={showShareModal}
              onOpenChange={setShowShareModal}
              lessonId={selectedLesson.id}
              lessonTitle={selectedLesson.title}
              onShareSuccess={loadLessons}
            />
          )}
        </>
      )}
    </div>
  );
}